import { Button, Dropdown, Menu, Radio, Message } from "@arco-design/web-react";

import "./ConversationsList.css";
import { IconPlus } from '@arco-design/web-react/icon';
import { GroupChannel, User } from "@sendbird/chat/lib/__definition";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { GroupChannelList } from '@sendbird/uikit-react/GroupChannelList';
import {
  GroupChannelListItemBasicProps
} from "@sendbird/uikit-react/types/modules/GroupChannelList/components/GroupChannelListItem/GroupChannelListItemView";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../../../routes/routes";
import { RootState } from '../../../../store/store';
import { getButtonColor, getButtonTextColor } from '../../../../utils/cssVariables';
import { MemberWithMetaData } from "../../types";
import { getUserFromConversation } from "../../utils/getUserFromConversation";
import { ensureSendbirdUsers } from "../../utils/platformUserSearch";
import { NewChatButton } from '../NewChatButton/NewChatButton';
import { StartConversationDialog } from "../StartConversationDialog/StartConversationDialog";
import { StartGroupChatDialog } from '../StartGroupChatDialog/StartGroupChatDialog';
import { ConversationPreview } from "./ConversationPreview/ConversationPreview";
import { useGroupChannelCount } from "../../utils/useGroupChannelCount";
import { useQueryClient } from "@tanstack/react-query";
import { useBreakpoint } from "../../../../utils/hooks/useBreakpoint";
import { GROUP_CHAT_CUSTOM_TYPE, SYSTEM_MODERATOR_USER_ID } from "../../constants";
import { useGuestActionGuard } from "../../../../hooks/useGuestActionGuard";

type Props = {
  selectedConversation: GroupChannel | null;
  onConversationSelected: (channel: GroupChannel | null) => void;
}

export function ConversationsList({ selectedConversation, onConversationSelected }: Props) {
  const { t } = useTranslation();
  const { guardAction, handleGuestApiError } = useGuestActionGuard();
  const { state } = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const isGroupChatsEnabled = useSelector((state: RootState) => state.config.isGroupChatsEnabled);

  const queryClient = useQueryClient();
  const { data: groupChannelCount } = useGroupChannelCount();
  const showGroupChats = groupChannelCount && groupChannelCount > 0 && isGroupChatsEnabled;

  const RadioGroup = Radio.Group;
  const [showNewContactModal, setShowNewContactModal] = useState<boolean>(false);
  const [showGroupContactsModal, setShowGroupContactsModal] = useState<boolean>(false);
  const [chatsFilter, setChatsFilter] = useState<string>('all');

  const { isMobile } = useBreakpoint('mobile');
  const { stores: { sdkStore: { sdk }, userStore } } = useSendbirdStateContext();

  const showFloatingFab = Boolean(isGroupChatsEnabled && !isMobile);
  const showHeaderCompose = !isGroupChatsEnabled || isMobile;

  useEffect(() => {
    if (!sdk.groupChannel || !state?.userId) {
      return;
    }

    const started = guardAction(() => {
      const params = {
        invitedUserIds: [state.userId],
        name: `${userStore.user.nickname} (${userStore.user.userId}) - ${state.name} (${state.userId})`,
        customType: state.isMentor ? 'withMentor' : 'withMentee',
        isDistinct: true,
        operatorUserIds: [SYSTEM_MODERATOR_USER_ID],
      };

      sdk.groupChannel.createChannel(params).then((channel: GroupChannel) => {
        onConversationSelected(channel);
        navigate(routes.MESSAGES, { state: null });
      }).catch((error) => {
        handleGuestApiError(error);
      });
    });

    if (!started) {
      navigate(routes.MESSAGES, { state: null, replace: true });
    }
  }, [state?.userId, sdk.groupChannel]);

  const chatsFilterOptions = [
    {
      value: 'all',
      label: t('messages.all_messages_tab'),
    },
    {
      value: 'withMentor',
      label: t('messages.mentors_messages_tab'),
    },
    {
      value: 'withMentee',
      label: t('messages.mentees_messages_tab'),
    },
    ...(showGroupChats ? ([{ value: 'group', label: t('messages.group_chat_tab'), }]) : []),
  ];

  const handleCreateNewChannel = async (user: User) => {
    try {
      await ensureSendbirdUsers([user.userId]);
    } catch (error) {
      if (handleGuestApiError(error)) {
        return;
      }
      throw error;
    }

    const params = {
      invitedUserIds: [user.userId],
      name: `${userStore.user.nickname}(${userStore.user.userId}) - ${user.nickname}(${user.userId})`,
      isDistinct: true,
      operatorUserIds: [SYSTEM_MODERATOR_USER_ID],
    };

    sdk.groupChannel.createChannel(params).then((channel: GroupChannel) => {
      onConversationSelected(channel);
    });

    queryClient.invalidateQueries({ queryKey: ['groupChannelCount'] })
  };

  const handleCreateNewGroupChat = async (name: string, users: User[]) => {
    const userIds = users.map((user) => user.userId);
    try {
      await ensureSendbirdUsers(userIds);
    } catch (error) {
      if (handleGuestApiError(error)) {
        return;
      }
      throw error;
    }

    const creatorId = userStore.user.userId;
    const params = {
      invitedUserIds: userIds,
      name: name,
      isDistinct: false,
      operatorUserIds: [creatorId],
      customType: GROUP_CHAT_CUSTOM_TYPE,
    };

    try {
      const channel = await sdk.groupChannel.createChannel(params);
      onConversationSelected(channel);
      queryClient.invalidateQueries({ queryKey: ["groupChannelCount"] });
    } catch (error) {
      console.error("Failed to create group chat:", error);
      Message.error({
        content: t("messages.create_group_chat_error"),
        closable: true,
        duration: 4000,
      });
      throw error;
    }
  };

  const isMatchFilter = (respondent: MemberWithMetaData | null, groupChannel: GroupChannelListItemBasicProps) => {
    const hideGroupChat = !isGroupChatsEnabled && groupChannel.channel.customType === 'group';

    if (chatsFilter === 'all' || !respondent) {
      return !hideGroupChat;
    }

    if (chatsFilter === 'group') {
      return groupChannel.channel.customType === 'group';
    }

    if (chatsFilter === 'withMentor') {
      return respondent?.metaData?.isMentor === 'true' && groupChannel.channel.customType !== 'group';
    }

    if (chatsFilter === 'withMentee') {
      return respondent?.metaData?.isMentor === 'false' && groupChannel.channel.customType !== 'group';
    }

    return true;
  };

  const openNewContactModal = () => guardAction(() => setShowNewContactModal(true));
  const openGroupContactsModal = () => guardAction(() => setShowGroupContactsModal(true));

  const createChatMenu = (
    <Menu>
      <Menu.Item key="1" onClick={openNewContactModal}>{t('messages.direct_message_button')}</Menu.Item>
      <Menu.Item key="2" onClick={openGroupContactsModal}>{t('messages.group_chat_button')}</Menu.Item>
    </Menu>
  );

  const composeButtonStyle = {
    color: getButtonTextColor(),
    backgroundColor: getButtonColor(),
  };

  const renderComposeButton = () => {
    if (user?.isAdmin && isGroupChatsEnabled) {
      return (
        <Dropdown droplist={createChatMenu} position={isMobile ? 'br' : 'tr'} trigger="click">
          <Button
            style={composeButtonStyle}
            className="add-contact-button"
            size="large"
            shape="circle"
            aria-label={t('messages.direct_message_button')}
          >
            <IconPlus fontSize={24} />
          </Button>
        </Dropdown>
      );
    }

    return (
      <NewChatButton onClick={openNewContactModal} />
    );
  };

  return (
    <div className="conversations-list">
      <StartConversationDialog
        visible={showNewContactModal}
        onClose={() => setShowNewContactModal(false)}
        onMessagingStart={handleCreateNewChannel}
      />
      <StartGroupChatDialog
        visible={showGroupContactsModal}
        onClose={() => setShowGroupContactsModal(false)}
        onMessagingStart={handleCreateNewGroupChat}
      />
      {showFloatingFab && (
        <div className="conversations-list__add-button-wrapper">
          {renderComposeButton()}
        </div>
      )}
      <GroupChannelList
        renderHeader={() => {
          return (
            <div className="conversations-list-header">
              <RadioGroup
                options={chatsFilterOptions}
                size="default"
                type="button"
                value={chatsFilter}
                onChange={setChatsFilter}
              />
              {showHeaderCompose && renderComposeButton()}
            </div>
          );
        }}
        allowProfileEdit={false}
        disableUserProfile
        onChannelCreated={() => {
        }}
        onChannelSelect={() => {
        }}
        renderChannelPreview={(channel: GroupChannelListItemBasicProps) => {
          const respondent = getUserFromConversation(channel.channel, userStore.user);

          if (!isMatchFilter(respondent, channel)) {
            return <div />;
          }

          return (
            <ConversationPreview onClick={() => onConversationSelected(channel.channel)} conversation={channel}
                                 isSelected={channel.channel.url === selectedConversation?.url} />
          );
        }}
      />
    </div>
  );
}